import { Typography } from '@mui/material';
import { ReactNode } from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';

interface GridListLabelProps {
  /** Intl message for the label when no interpolation is needed. Prefer this when extractable. */
  message?: MessageDescriptor;
  /**
   * Pre-rendered label when `message` is omitted — use for plain text or a
   * `<FormattedMessage>` that needs `values`.
   */
  children?: ReactNode;
  /** When true, sticks to the top of the scroll container while the group is in view. */
  sticky?: boolean;
}

/**
 * Low-priority uppercase label for date-grouped grids and feed sections.
 * Skippable wayfinding — the eye should land on the items, not the label.
 */
export function GridListLabel({ message, children, sticky = false }: GridListLabelProps) {
  const content = message ? <FormattedMessage {...message} /> : children;

  return (
    <Typography
      variant="caption"
      color="text.secondary"
      component="div"
      sx={{
        // Only space above when another label precedes this one as a sibling.
        // Wrapped in a section Box (status / fragments), the label is always
        // :first-of-type so section mb handles gaps and the page top stays flush.
        mt: 3,
        mb: 1.5,
        '&:first-of-type': { mt: 0 },
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        ...(sticky && {
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: 'background.default',
          py: 1,
        }),
      }}
    >
      {content}
    </Typography>
  );
}
